import React, { useCallback, useEffect, useState } from 'react';

import {
  Assignment,
  AssignmentTurnedIn,
  BarChart,
  CloudDownload,
  Dashboard,
  Hub,
  List,
  Map,
  PinDrop,
  SwapCallsRounded,
} from '@mui/icons-material';
import { useLiveQuery } from 'dexie-react-hooks';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Permission } from '@cnx-saas/common';
import { CarrierNetwork, Parcel } from '@cnx-saas/design-system';
import { DexieIndexedDb, PushEventType } from '@cnx-saas/indexed-db';
import { useAuth } from '@cnx-saas/react-auth';

import { useTopicSubscriptions } from '../../../api/subscriptions/queries/use-topic-subscriptions';
import {
  useGetMeLazyQuery,
  useGetMyTenantLazyQuery,
} from '../../../api/tenant';
import { BeforeInstallPromptEvent } from './before-install-prompt-event';
import { useShipmentEventsCount } from './hooks';
import { Sidebar } from './sidebar';
import { SidebarWrapper } from './sidebar.style';
import { INavigationSection, PathEnum } from './sidebar.type';
import { SidebarTranslationKeys } from './translation-keys';

function SidebarContainer(): JSX.Element | null {
  const { authorized } = useAuth(true);

  const location = useLocation();
  const { t } = useTranslation();
  const [navigationSections, setNavigationSections] = useState<
    INavigationSection[]
  >([]);

  const [user, setUser] = useState<{
    name: string;
    companyName: string;
    dashboardId?: string;
  }>({
    name: '',
    companyName: '',
  });

  const [getMe] = useGetMeLazyQuery();
  const [getMyTenant] = useGetMyTenantLazyQuery();

  const [pwaInstallEvent, setPwaInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);

  React.useEffect(() => {
    const saveInstallEvent = (e: Event) => {
      setPwaInstallEvent(e as unknown as BeforeInstallPromptEvent);
      window.removeEventListener('beforeinstallprompt', saveInstallEvent);
    };

    window.addEventListener('beforeinstallprompt', saveInstallEvent);
  });

  React.useEffect(() => {
    void getMe().then(({ data }) => {
      if (!data) {
        return;
      }
      const me = data.getMe;
      setUser((prevState) => ({
        ...prevState,
        name: `${me.firstName} ${me.lastName}`,
      }));
    });

    void getMyTenant().then(({ data }) => {
      if (!data) {
        return;
      }
      const tenant = data.getMyTenant;
      setUser((prevState) => ({
        ...prevState,
        companyName: tenant.name,
        dashboardId: tenant.dashboardId,
      }));
    });
    return;
  }, [getMe, getMyTenant]);

  const isAllowedToSeeShipmentExecution = authorized([
    Permission.ShipmentExecution,
  ]);

  const isAllowedToSeeTourPlaner = authorized([Permission.TourPlanner]);
  const isAllowedToSeeOrderManagement = authorized([
    Permission.OrderManagement,
  ]);

  useTopicSubscriptions();

  const tourPlanPlannedPushEventsCount =
    useLiveQuery(async () => {
      return DexieIndexedDb.getInstance().getPushEvents({
        type: PushEventType.TourplanPlanned,
      });
    })?.length ?? 0;

  const shipmentUpdatesCount = useShipmentEventsCount();

  const getNavigationSections = useCallback(
    (dashboardId?: string) => {
      const isPathActive = (path: PathEnum): boolean => {
        const pathWithSearch = location.pathname + location.search;
        return pathWithSearch.startsWith(path);
      };

      const navigationSections: INavigationSection[] = [
        {
          label: t(SidebarTranslationKeys.OVERVIEW_SECTION_LABEL),
          navigationLinks: [
            {
              path: PathEnum.PATH_DASHBOARD,
              active: isPathActive(PathEnum.PATH_DASHBOARD),
              label: t(SidebarTranslationKeys.OVERVIEW_SECTION_DASHBOARD),
              icon: <Dashboard />,
              isShown: true,
            },
            {
              path: PathEnum.PATH_ANALYTICS,
              active: isPathActive(PathEnum.PATH_ANALYTICS),
              label: t(SidebarTranslationKeys.OVERVIEW_ANALYTICS),
              icon: <BarChart />,
              isShown: dashboardId ? true : false,
            },
          ],
        },
        {
          label: t(SidebarTranslationKeys.MY_LOADS_SECTION_LABEL),
          navigationLinks: [
            {
              active: location.pathname.endsWith(PathEnum.PATH_ORDERS_LOADS),
              label: t(SidebarTranslationKeys.MY_DATA_SECTION_LOADS),
              path: PathEnum.PATH_ORDERS_LOADS,
              icon: <Parcel />,
              isShown: !isAllowedToSeeOrderManagement,
            },
          ].filter(({ isShown }) => isShown),
        },
        {
          label: t(SidebarTranslationKeys.ORDERS_SECTION_LABEL),
          navigationLinks: [
            {
              path: PathEnum.PATH_ORDERS,
              active: location.pathname.endsWith(PathEnum.PATH_ORDERS),
              label: t(SidebarTranslationKeys.ORDERS_SECTION_LABEL),
              icon: <List />,
              isShown: isAllowedToSeeOrderManagement,
            },
            {
              path: PathEnum.PATH_ORDER_PLANNING,
              active: location.pathname.endsWith(PathEnum.PATH_ORDER_PLANNING),
              label: t('order-planning.order-planning.label', {
                defaultValue: 'Order planning',
              }),
              icon: <SwapCallsRounded />,
              isShown: isAllowedToSeeShipmentExecution
                ? false
                : isAllowedToSeeOrderManagement,
            },
            {
              path: PathEnum.PATH_ORDERS_LOADS,
              active: location.pathname.endsWith(PathEnum.PATH_ORDERS_LOADS),
              label: t(SidebarTranslationKeys.MY_DATA_SECTION_LOADS),
              icon: <Parcel />,
              isShown: isAllowedToSeeOrderManagement,
            },
          ].filter(({ isShown }) => isShown),
        },
        {
          label: t(SidebarTranslationKeys.MY_TOUR_PLANNER_SECTION_LABEL),
          navigationLinks: [
            {
              path: PathEnum.PATH_TOUR_PLANNER,
              active: location.pathname.endsWith(PathEnum.PATH_TOUR_PLANNER),
              label: t(SidebarTranslationKeys.TOUR_PLANNER_SECTION),
              icon: <Map />,
              isShown: isAllowedToSeeTourPlaner,
              updatesCount: tourPlanPlannedPushEventsCount,
            },
          ],
        },
        {
          label: t(SidebarTranslationKeys.MY_SHIPMENTS_SECTION_LABEL),
          navigationLinks: [
            {
              path: PathEnum.PATH_SHIPMENT_EXECUTION,
              active: location.pathname.endsWith(
                PathEnum.PATH_SHIPMENT_EXECUTION,
              ),
              label: t(
                SidebarTranslationKeys.MY_SHIPMENTS_SECTION_SHIPMENT_EXECUTION,
              ),
              icon: <AssignmentTurnedIn />,
              isShown: isAllowedToSeeShipmentExecution,
              updatesCount: shipmentUpdatesCount,
            },
            {
              path: PathEnum.PATH_DATA_EXPORT,
              active: location.pathname.endsWith(PathEnum.PATH_DATA_EXPORT),
              label: t(SidebarTranslationKeys.MY_SHIPMENTS_SECTION_DATA_EXPORT),
              icon: <CloudDownload />,
              isShown: isAllowedToSeeShipmentExecution,
            },
          ].filter(({ isShown }) => isShown),
        },
        {
          label: t(SidebarTranslationKeys.MY_DATA_SECTION_LABEL),
          navigationLinks: [
            {
              path: PathEnum.PATH_ARTICLES,
              active: location.pathname.endsWith(PathEnum.PATH_ARTICLES),
              label: t(SidebarTranslationKeys.PATH_ARTICLES),
              icon: <Assignment />,
              isShown: isAllowedToSeeOrderManagement,
            },
            {
              active: location.pathname.startsWith(
                PathEnum.PATH_CARRIER_NETWORK,
              ),
              label: t(SidebarTranslationKeys.MY_DATA_SECTION_CARRIER_NETWORK),
              path: PathEnum.PATH_CARRIER_NETWORK,
              icon: <Hub />,
              isShown: isAllowedToSeeShipmentExecution,
            },
            {
              active: location.pathname.startsWith(PathEnum.PATH_VEHICLES),
              label: t(SidebarTranslationKeys.MY_DATA_SECTION_VEHICLES),
              path: PathEnum.PATH_FLEETS,
              icon: <CarrierNetwork />,
              isShown: true,
            },
            {
              active: location.pathname.startsWith(PathEnum.PATH_LOCATIONS),
              label: t(SidebarTranslationKeys.MY_DATA_SECTION_LOCATIONS),
              path: PathEnum.PATH_LOCATIONS,
              icon: <PinDrop />,
              isShown: true,
            },
          ].filter(({ isShown }) => isShown),
        },
      ]
        // instead of filtering each navigation section filter out all navigation sections at one step
        .reduce((acc: INavigationSection[], navigationSection) => {
          const { navigationLinks } = navigationSection;
          const visibleNavigationLinks = navigationLinks.filter(
            ({ isShown }) => isShown,
          );

          if (visibleNavigationLinks.length > 0) {
            acc.push({
              ...navigationSection,
              navigationLinks: visibleNavigationLinks,
            });
          }

          return acc;
        }, []);
      return navigationSections;
    },
    [
      isAllowedToSeeOrderManagement,
      isAllowedToSeeShipmentExecution,
      isAllowedToSeeTourPlaner,
      location.pathname,
      location.search,
      shipmentUpdatesCount,
      t,
      tourPlanPlannedPushEventsCount,
    ],
  );

  useEffect(() => {
    const sections = getNavigationSections(user.dashboardId);
    setNavigationSections(sections);
  }, [user, location.pathname, getNavigationSections]);

  return (
    <SidebarWrapper>
      <Sidebar
        homePath={PathEnum.PATH_DASHBOARD}
        navigationSections={navigationSections}
        userName={user.name}
        userCompanyName={user.companyName}
        pwaInstallEvent={pwaInstallEvent}
      />
    </SidebarWrapper>
  );
}

export { SidebarContainer };
